import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {API} from '@configs/api.config';

/**
 * Request header asking ms-jobfile to serve legs with their route id only (`encodedPath` left out where
 * an `encodedPathId` exists). See ms-jobfile LegPathResponseAdvice.
 */
export const LEG_PATHS_HEADER = 'X-Leg-Paths';
export const LEG_PATHS_IDS_ONLY = 'ids';

/** A leg as ms-jobfile serves it: the routed path, and/or the id it is stored under. */
export interface RouteLeg {
  encodedPath?: string;
  encodedPathId?: string;
}

interface RoutePolyline {
  id: string;
  encodedPath: string;
}

/**
 * Leg route polylines by id (`encodedPathId`).
 *
 * ms-jobfile stores each distinct leg route once and serves legs with its id. Responses still carry
 * `encodedPath` for now; once they stop, the legs a screen draws are filled from here instead of every
 * job file response carrying every load's routes. Ids are content hashes, so a fetched route never
 * changes and is kept for the session.
 */
@Injectable({
  providedIn: 'root'
})
export class RoutePolylineService {

  /** Limit of the batch endpoint. */
  static readonly MAX_BATCH_SIZE = 500;
  /** Routes kept for the session, oldest dropped first. A route is ~20KB. */
  static readonly MAX_CACHED = 1000;

  private readonly paths = new Map<string, string>();

  constructor(private readonly http: HttpClient) {}

  /**
   * Fills `encodedPath` of the given legs that carry only an `encodedPathId`, then emits `value`. Legs
   * keep a path they already have. A failed lookup leaves those legs without a path - maps then route
   * them again - rather than failing the caller.
   */
  fillLegPaths<T>(value: T, legs: RouteLeg[]): Observable<T> {
    const known = (legs || []).filter(leg => !!leg);
    known.filter(leg => leg.encodedPathId && leg.encodedPath)
      .forEach(leg => this.remember(leg.encodedPathId, leg.encodedPath));

    const missing = Array.from(new Set(known
      .filter(leg => leg.encodedPathId && !leg.encodedPath && !this.paths.has(leg.encodedPathId))
      .map(leg => leg.encodedPathId)));
    if (missing.length === 0) {
      this.apply(known);
      return of(value);
    }

    const batches: string[][] = [];
    for (let i = 0; i < missing.length; i += RoutePolylineService.MAX_BATCH_SIZE) {
      batches.push(missing.slice(i, i + RoutePolylineService.MAX_BATCH_SIZE));
    }
    return forkJoin(batches.map(ids => this.http.post<RoutePolyline[]>(API.jobFile.getRoutePolylines, ids))).pipe(
      map(results => {
        results.forEach(polylines => (polylines || []).forEach(p => this.remember(p.id, p.encodedPath)));
        this.apply(known);
        return value;
      }),
      catchError(error => {
        console.error('Could not load leg routes', error);
        this.apply(known);
        return of(value);
      })
    );
  }

  /**
   * A copy of `container` whose legs that carry only an `encodedPathId` have their `encodedPath`, for
   * drawing one load. The container and its legs are not modified, so it is safe on store state.
   * Emits the container itself when nothing needs filling.
   */
  withLegPaths<T extends { legs?: RouteLeg[] }>(container: T): Observable<T> {
    const legs = container?.legs;
    if (!legs?.some(leg => leg?.encodedPathId && !leg.encodedPath)) {
      return of(container);
    }
    const copies = legs.map(leg => (leg ? {...leg} : leg));
    return this.fillLegPaths(copies, copies).pipe(map(filled => ({...container, legs: filled} as T)));
  }

  private apply(legs: RouteLeg[]): void {
    legs.filter(leg => leg.encodedPathId && !leg.encodedPath && this.paths.has(leg.encodedPathId))
      .forEach(leg => leg.encodedPath = this.paths.get(leg.encodedPathId));
  }

  private remember(id: string, path: string): void {
    if (!id || !path) {
      return;
    }
    this.paths.delete(id);
    this.paths.set(id, path);
    if (this.paths.size > RoutePolylineService.MAX_CACHED) {
      this.paths.delete(this.paths.keys().next().value);
    }
  }
}
